import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Axios from "axios";
import AxiosInstance from "../../AxiosInstance";
import StateContext from "../../Contexts/StateContext";
import { 
  Grid, Typography, CircularProgress, Container, Box, 
  Button, Card, Divider, Avatar, TextField, Snackbar, Alert, Chip,
  Autocomplete, Paper
} from "@mui/material";
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';

// Utils
import { formatPhoneNumber } from "../../Utils/PhoneUtils";

import defaultProfilePicture from "../../Assets/defaultProfilePicture.jpg";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ChatIcon from '@mui/icons-material/Chat';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import StarIcon from '@mui/icons-material/Star';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';

const defaultIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});

function DayLabourerDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const GlobalState = useContext(StateContext);
  const [labourer, setLabourer] = useState(null);
  const [allLabourers, setAllLabourers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [openSnack, setOpenSnack] = useState(false);
  const [bookingStatus, setBookingStatus] = useState('success');
  const [linkLabourerId, setLinkLabourerId] = useState(null);
  const { config, environment } = require('../../index');

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function fetchData() {
      try {
        const response = await AxiosInstance.get(`${config[environment].businessesEndpoint}${id}/`, { cancelToken: source.token });
        setLabourer(response.data);
        
        // If user might be admin, fetch all registered labourers for linking
        const allRes = await AxiosInstance.get(`${config[environment].businessesEndpoint}?type=DayLabourer`, { cancelToken: source.token });
        setAllLabourers(allRes.data.filter(l => !l.is_recommended && l.id !== parseInt(id)));
        
        setLoading(false);
      } catch (error) {
        if (!Axios.isCancel(error)) {
          setLoading(false);
        }
      }
    }
    fetchData();
    return () => source.cancel();
  }, [id, config, environment]);

  const handleLinkLabourer = async () => {
    try {
      await AxiosInstance.patch(`${config[environment].businessesEndpoint}${id}/`, {
        linked_master: linkLabourerId
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setBookingStatus('success');
      setOpenSnack(true);
      // Refresh data
      const response = await AxiosInstance.get(`${config[environment].businessesEndpoint}${id}/`);
      setLabourer(response.data);
    } catch (error) {
      setBookingStatus('error');
      setOpenSnack(true);
    }
  };

  const handleBooking = async () => {
    try {
      await AxiosInstance.post(config[environment].businessBookingsEndpoint, {
        business: id,
        date: selectedDate.format('YYYY-MM-DD'),
        message: t('Business.BookingFromWebsite', "Foglalás a weboldalról")
      }, {
        headers: {
          Authorization: `Bearer ${GlobalState.jwtAccessToken}`
        }
      });
      setBookingStatus('success');
      setOpenSnack(true);
    } catch (error) {
      setBookingStatus('error');
      setOpenSnack(true);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(t('Common.DeleteConfirm'))) {
      try {
        try {
          await AxiosInstance.delete(`${config[environment].businessesEndpoint}${id}/delete/`);
        } catch (delErr) {
          if (delErr.response && (delErr.response.status === 405 || delErr.response.status === 404)) {
            await AxiosInstance.delete(`${config[environment].businessesEndpoint}${id}/`);
          } else {
            throw delErr;
          }
        }
        setBookingStatus('success');
        setOpenSnack(true);
        setTimeout(() => {
          if (labourer.business_type === 'Master') {
            navigate(`/${i18n.language}/mesterek`);
          } else if (labourer.business_type === 'DayLabourer') {
            navigate(`/${i18n.language}/napszamosok`);
          } else {
            navigate(`/${i18n.language}/cegek`);
          }
        }, 2000);
      } catch (error) {
        setBookingStatus('error');
        setOpenSnack(true);
      }
    }
  };

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ height: "100vh" }}>
        <CircularProgress />
      </Grid>
    );
  }

  if (!labourer) return <Typography align="center" sx={{ mt: 5 }}>{t('Business.LabourerNotFound', 'Day labourer not found')}</Typography>;

  const whatsappUrl = labourer.phone_number ? `https://wa.me/${labourer.phone_number.replace(/\D/g, '')}` : null;
  const messengerUrl = labourer.facebook_url ? labourer.facebook_url.replace('facebook.com', 'm.me') : null;

  const isOwner = labourer && GlobalState.userId && parseInt(labourer.owner) === parseInt(GlobalState.userId);

  const editPath = labourer ? (
    labourer.business_type === 'Master' ? `/${i18n.language}/mester/szerkesztes/${id}` :
    labourer.business_type === 'Company' ? `/${i18n.language}/ceg/szerkesztes/${id}` :
    `/${i18n.language}/napszamos/szerkesztes/${id}`
  ) : `/${i18n.language}/napszamos/szerkesztes/${id}`;

  const shouldDisableDate = (date) => {
    if (!labourer || !labourer.availabilities) return false;
    const dateStr = date.format('YYYY-MM-DD');
    const availability = labourer.availabilities.find(a => a.date === dateStr);
    return availability ? !availability.is_available : false;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4, mt: '50px' }}>
      <Button onClick={() => navigate(-1)} sx={{ mb: 2 }}>{t('Common.Back')}</Button>
      
      {isOwner && (
        <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate(editPath)}
          >
            {t('Common.Edit')}
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDelete}
          >
            {t('Common.Delete')}
          </Button>
        </Box>
      )}

      <Grid container spacing={4}>
        {labourer.is_recommended && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#fffde7', border: '1px solid #ffd54f' }}>
              <Typography variant="h6" gutterBottom color="warning.dark">
                Admin: {t('Business.LinkToRegisteredProfile', 'Összekapcsolás regisztrált profillal')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Autocomplete
                  fullWidth
                  options={allLabourers}
                  getOptionLabel={(option) => option.name || ""}
                  value={allLabourers.find(m => m.id === linkLabourerId) || null}
                  onChange={(event, newValue) => {
                    setLinkLabourerId(newValue ? newValue.id : null);
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label={t('Business.SearchRegisteredMaster', 'Regisztrált szakember keresése')} 
                      size="small"
                    />
                  )}
                />
                <Button 
                  variant="contained" 
                  onClick={handleLinkLabourer}
                  disabled={!linkLabourerId}
                  startIcon={<LinkIcon />}
                >
                  {t('Business.LinkButton', 'Társítás')}
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={5}>
          <Card sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5', height: '100%' }}>
            <Avatar 
              src={labourer.picture || defaultProfilePicture} 
              sx={{ width: 150, height: 150, mx: 'auto', mb: 2, border: '3px solid #105b79' }}
            />
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#105b79' }}>
              {labourer.name}
            </Typography>

            {labourer.is_recommended && (
              <Box sx={{ mt: 1, mb: 2 }}>
                <Chip 
                  label={t('Business.RecommendedStatus', 'Ajánlott szakember')} 
                  color="secondary" 
                  variant="outlined" 
                  icon={<StarIcon />}
                  sx={{ mb: 1 }}
                />
                {labourer.recommender_name && (
                  <Typography variant="body2" color="text.secondary">
                    {t('Business.RecommendedBy', 'Ajánlotta')}: {labourer.recommender_name}
                  </Typography>
                )}
                {labourer.linked_master_details && (
                  <Button 
                    startIcon={<PersonIcon />}
                    onClick={() => navigate(`/napszamos/${labourer.linked_master_details.id}`)}
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    {t('Business.ViewRegisteredProfile', 'Regisztrált profil megtekintése')}
                  </Button>
                )}
              </Box>
            )}

            <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
              {labourer.description || t('Common.NoBio', 'Nincs leírás megadva.')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, textAlign: 'left' }}>
              {labourer.phone_number && (
                <Typography variant="body2">
                  <strong>{t('Administration.PhoneNumber')}:</strong> {formatPhoneNumber(labourer.phone_number)}
                </Typography>
              )}
              {labourer.email && (
                <Typography variant="body2">
                  <strong>Email:</strong> {labourer.email}
                </Typography>
              )}
              {labourer.website && (
                <Typography variant="body2">
                  <strong>{t('Business.Website', 'Honlap')}:</strong> <a href={labourer.website.startsWith('http') ? labourer.website : `https://${labourer.website}`} target="_blank" rel="noopener noreferrer">{labourer.website}</a>
                </Typography>
              )}
              {labourer.szek && (
                <Typography variant="body2">
                  <strong>{t('Events.Szek')}:</strong> {labourer.szek}
                </Typography>
              )}
              {labourer.kozsegvaros && (
                <Typography variant="body2">
                  <strong>{t('Events.Kozseg')}:</strong> {labourer.kozsegvaros}
                </Typography>
              )}
              {labourer.telepules && (
                <Typography variant="body2">
                  <strong>{t('Events.Telepules')}:</strong> {labourer.telepules}
                </Typography>
              )}
              {labourer.address && (
                <Typography variant="body2">
                  <strong>{t('Thematic.Address')}:</strong> {labourer.address}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {whatsappUrl && (
                <Button 
                  variant="contained" 
                  color="success" 
                  startIcon={<WhatsAppIcon />}
                  href={whatsappUrl}
                  target="_blank"
                >
                  WhatsApp
                </Button>
              )}
              {messengerUrl && (
                <Button 
                  variant="contained" 
                  sx={{ bgcolor: '#0084FF', '&:hover': { bgcolor: '#0073e6' } }}
                  startIcon={<ChatIcon />}
                  href={messengerUrl}
                  target="_blank"
                >
                  Messenger
                </Button>
              )}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
              {t('Business.BookDayLabourer', 'Napszámos foglalása')}
            </Typography>
            
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ mb: 3 }}>
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  renderInput={(params) => <TextField {...params} />}
                  minDate={dayjs()}
                  shouldDisableDate={shouldDisableDate}
                />
              </Box>
            </LocalizationProvider>

            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t('Business.SelectedDate', 'Kiválasztott dátum')}: {selectedDate.format('YYYY-MM-DD')}
              </Typography>
              <Button 
                variant="contained" 
                size="large" 
                onClick={handleBooking}
                sx={{ bgcolor: '#105b79' }}
              >
                {t('Business.ConfirmBooking', 'Foglalás megerősítése')}
              </Button>
            </Box>
          </Card>

          {(labourer.latitude && labourer.longitude) && (
            <Card sx={{ mt: 3, p: 2 }}>
              <Typography variant="h6" gutterBottom>{t('Common.Location', 'Helyszín')}</Typography>
              <Box sx={{ height: 300, width: '100%', borderRadius: 1, overflow: 'hidden' }}>
                <MapContainer center={[labourer.latitude, labourer.longitude]} zoom={13} style={{ height: '100%', width: '100%' }}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <Marker position={[labourer.latitude, labourer.longitude]} icon={defaultIcon} />
                </MapContainer>
              </Box>
              {labourer.address && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>{t('Thematic.Address')}:</strong> {labourer.address}
                </Typography>
              )}
              {(labourer.szek || labourer.kozsegvaros || labourer.telepules) && (
                <Typography variant="body2" color="text.secondary">
                  {[labourer.szek, labourer.kozsegvaros, labourer.telepules].filter(Boolean).join(' - ')}
                </Typography>
              )}
            </Card>
          )}
        </Grid>
      </Grid>

      <Snackbar open={openSnack} autoHideDuration={6000} onClose={() => setOpenSnack(false)}>
        <Alert onClose={() => setOpenSnack(false)} severity={bookingStatus} sx={{ width: '100%' }}>
          {bookingStatus === 'success' 
            ? (t('Common.DeleteSuccess') ? t('Common.DeleteSuccess') : t('Business.BookingSuccess', 'Sikeres foglalás!'))
            : (t('Common.DeleteError') ? t('Common.DeleteError') : t('Business.BookingError', 'Hiba történt a foglalás során.'))}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default DayLabourerDetail;